import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { tenantKeys, useMyTenant } from '@/entities/tenant/queries';

/**
 * KOVIX-380/63 (13/10/2026): banner chỉ dựa vào GET /tenant/me (admin.tenant.overview, tải MỘT lần khi Shell mở — refetchOnWindowFocus tắt) nên không hiện
 * cho người thiếu quyền đó, và không hiện nếu tenant chuyển TERMINATING khi trang đang mở. Gateway trả 403 tenant.terminating_read_only /
 * tenant.suspended_read_only cho mọi thao tác ghi → ghi nhận mã đó (MutationCache ở main.tsx) làm nguồn dự phòng + tải lại /tenant/me.
 */
type ReadOnlyStatus = 'SUSPENDED' | 'TERMINATING';
const READ_ONLY_CODES: Record<string, ReadOnlyStatus> = {
  'tenant.suspended_read_only': 'SUSPENDED',
  'tenant.terminating_read_only': 'TERMINATING',
};
let observed: ReadOnlyStatus | null = null;
const listeners = new Set<() => void>();

/** Gọi khi một yêu cầu bị gateway từ chối; mã khác bỏ qua. Trả true khi mã là "tenant chỉ đọc". */
export function noteTenantReadOnly(code: string | undefined): boolean {
  const s = code ? READ_ONLY_CODES[code] : undefined;
  if (!s) return false;
  if (observed !== s) {
    observed = s;
    listeners.forEach((l) => l());
  }
  return true;
}

/** Gỡ trạng thái dự phòng (ghi thành công / /tenant/me báo đang hoạt động). */
export function clearTenantReadOnly() {
  if (observed === null) return;
  observed = null;
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

/**
 * KOVIX-62 bước 6 (BR-18.123, 12/10/2026): tenant SUSPENDED (đình chỉ do quá hạn thanh toán) / TERMINATING (đóng băng chấm dứt) → banner toàn trang,
 * vì gateway chỉ cho đọc và mọi thao tác ghi trả 403 — trước đây màn hình vẫn "bình thường", người dùng chỉ thấy toast đỏ khi bấm.
 * Trạng thái đọc từ GET /tenant/me (admin.tenant.overview) khi {@code enabled}; {@code visible} (ngữ cảnh tenant, không phiên hỗ trợ) cho phép hiện theo mã 403 gateway.
 */
export function TenantStatusBanner({ enabled, visible = enabled }: { enabled: boolean; visible?: boolean }) {
  const { data } = useMyTenant(enabled);
  const fallback = useSyncExternalStore(subscribe, () => observed);
  const qc = useQueryClient();
  useEffect(() => {
    if (fallback && enabled) void qc.invalidateQueries({ queryKey: tenantKeys.me() });
  }, [fallback, enabled, qc]);
  // /tenant/me báo không còn chỉ đọc → gỡ banner dự phòng cũ
  useEffect(() => {
    if (data?.status && data.status !== 'SUSPENDED' && data.status !== 'TERMINATING') clearTenantReadOnly();
  }, [data?.status]);
  if (!visible && !enabled) return null;
  const status = (enabled ? data?.status : undefined) ?? (visible ? fallback : null);
  if (status === 'SUSPENDED')
    return (
      <div className="kx-ctxbar" role="alert" style={{ background: 'var(--kx-danger-bg, #fde8e8)', color: 'var(--kx-danger, #a00)' }}>
        ⛔ Dịch vụ đang <b>tạm ngưng do quá hạn thanh toán</b> — chỉ xem dữ liệu và tải hóa đơn, mọi thao tác ghi bị từ chối. Thanh toán hóa đơn SaaS
        để mở lại:{' '}
        <a href="/admin/subscription?tab=invoices">xem hóa đơn và dư nợ</a>
        .
      </div>
    );
  if (status === 'TERMINATING')
    return (
      <div className="kx-ctxbar" role="alert">
        ⚠ Tenant đang trong thời gian <b>chấm dứt</b> — chỉ xem và tải gói xuất dữ liệu, không ghi mới.
      </div>
    );
  return null;
}
